// src/components/Common/MyMealsStickyFooter.js
//
// Shared 4-button sticky bottom bar used on /mymeals (MyMealPlans),
// /mymeals/groceries (GroceriesPage), and /favorites (FavoriteMeals).
//
// The button whose page is currently active gets a light translucent
// background tint (bg-[rgba(255,255,255,0.6)]) to indicate selection.
//
// Props:
//   activeTab: 'meals' | 'groceries' | 'favourites'
//   displayTotalCost: string (already formatted with currency symbol), used
//                     for the "Groceries" badge. If null/undefined the
//                     badge shows "0" + country currency symbol.
//   userCountry: string — country name used to resolve the currency symbol
//                for the badge fallback.
//   isPremium: boolean — controls Create New behavior (direct vs drawer).
//   onStartNewMealPlan: () => void — called for premium users on Create New.
//   onOpenStartOverDrawer: () => void — called for non-premium users on Create New.
//   onOpenGroceries: () => void — fires when Groceries is tapped and we're NOT
//                                  already on the groceries page.
//   onOpenMeals: () => void — fires when Meals is tapped and we're NOT already
//                              on the meals page.
//   onOpenFavourites: () => void — fires when Favourites is tapped and we're
//                                   NOT already on the favourites page.

import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getCountryCurrency } from "../../services/countryService";

const ACTIVE_TINT = "bg-[rgba(255,255,255,0.6)]";

const BUTTON_BASE_CLASSES = `
  relative
  flex-1
  flex flex-col items-center gap-0 justify-center
  h-[48px] pt-[4px]
  rounded-[32px]
  text-[#0D0C07]
  font-normal
  text-[10.4px] leading-[16px]
  transition-colors
  md:hover:bg-[rgba(255,255,255,0.4)]
`;

const MyMealsStickyFooter = ({
  activeTab = "meals",
  displayTotalCost = null,
  userCountry = null,
  isPremium = false,
  onStartNewMealPlan,
  onOpenStartOverDrawer,
  onOpenGroceries,
  onOpenMeals,
  onOpenFavourites,
}) => {
  const { t } = useTranslation("mealplan");

  const isMealsActive = activeTab === "meals";
  const isGroceriesActive = activeTab === "groceries";
  const isFavouritesActive = activeTab === "favourites";

  const handleMealsClick = () => {
    if (isMealsActive) return;
    onOpenMeals && onOpenMeals();
  };

  const handleGroceriesClick = () => {
    if (isGroceriesActive) return;
    onOpenGroceries && onOpenGroceries();
  };

  const handleFavouritesClick = () => {
    if (isFavouritesActive) return;
    onOpenFavourites && onOpenFavourites();
  };

  const handleStartOverClick = () => {
    if (isPremium) {
      onStartNewMealPlan && onStartNewMealPlan();
    } else {
      onOpenStartOverDrawer && onOpenStartOverDrawer();
    }
  };

  const currencySymbol = userCountry
    ? getCountryCurrency(userCountry).symbol
    : "€";
  const numericValue = displayTotalCost
    ? Math.floor(parseFloat(displayTotalCost.replace(/[^0-9.]+/g, ""))) || 0
    : 0;
  const costBadge = `${numericValue}${currencySymbol}`;

  return createPortal(
    <div
      className="
        fixed bottom-[16px] left-[20px] right-[20px]
        py-1 px-1
        flex justify-center
        z-50
        mx-auto
        max-w-[390px]
        rounded-[96px]
        bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)]
        backdrop-blur-[64px]
        border-[1px] border-[#fff]
        shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
        transition-[bottom] duration-500 ease-in-out
      "
    >
      <div className="w-full gap-1 flex items-center justify-between">
        {/* Meals */}
        <button
          onClick={handleMealsClick}
          className={`${BUTTON_BASE_CLASSES} ${isMealsActive ? ACTIVE_TINT : ""}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M16.0002 2L13.7002 4.3C13.1505 4.86079 12.8426 5.61474 12.8426 6.4C12.8426 7.18526 13.1505 7.93921 13.7002 8.5L15.5002 10.3C16.0609 10.8497 16.8149 11.1576 17.6002 11.1576C18.3854 11.1576 19.1394 10.8497 19.7002 10.3L22.0002 8M15.0002 15L3.30016 3.3C2.90104 3.69105 2.58397 4.15782 2.36751 4.67295C2.15105 5.18809 2.03955 5.74123 2.03955 6.3C2.03955 6.85877 2.15105 7.41191 2.36751 7.92705C2.58397 8.44218 2.90104 8.90895 3.30016 9.3L10.6002 16.6C11.3002 17.3 12.6002 17.3 13.4002 16.6L15.0002 15ZM15.0002 15L22.0002 22M2.10016 21.8L8.50016 15.5M19.0002 5L12.0002 12"
              stroke="#0D0C07"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t("actions.meals")}</span>
        </button>

        {/* Groceries */}
        <button
          onClick={handleGroceriesClick}
          className={`${BUTTON_BASE_CLASSES} ${isGroceriesActive ? ACTIVE_TINT : ""}`}
        >
          {!isFavouritesActive && (
            <span className="absolute bg-[#FFCB00] px-1 rounded-[6px] right-[10%] top-[4px] text-[10.4px] leading-[16px]">
              {costBadge}
            </span>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M15.5 11L14.5 20M19.5 11L15.5 4M2.5 11H22.5M4 11L5.6 18.4C5.6935 18.8586 5.94485 19.2698 6.31028 19.5621C6.67572 19.8545 7.13211 20.0094 7.6 20H17.4C17.8679 20.0094 18.3243 19.8545 18.6897 19.5621C19.0552 19.2698 19.3065 18.8586 19.4 18.4L21.1 11M5 15.5H20M5.5 11L9.5 4M9.5 11L10.5 20"
              stroke="#0D0C07"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t("actions.groceries")}</span>
        </button>

        {/* Favourites */}
        <button
          onClick={handleFavouritesClick}
          className={`${BUTTON_BASE_CLASSES} ${isFavouritesActive ? ACTIVE_TINT : ""}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.06211 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z"
              stroke="#0D0C07"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t("actions.favourites")}</span>
        </button>

        {/* Create New */}
        <button
          onClick={handleStartOverClick}
          className={BUTTON_BASE_CLASSES}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M20 3.00001V7.00001M22 5.00001H18M4 17V19M5 18H3M9.937 15.5C9.84772 15.1539 9.66734 14.8381 9.41462 14.5854C9.1619 14.3327 8.84607 14.1523 8.5 14.063L2.365 12.481C2.26033 12.4513 2.16821 12.3883 2.10261 12.3014C2.03702 12.2146 2.00153 12.1088 2.00153 12C2.00153 11.8912 2.03702 11.7854 2.10261 11.6986C2.16821 11.6118 2.26033 11.5487 2.365 11.519L8.5 9.93601C8.84595 9.84681 9.16169 9.66658 9.4144 9.41404C9.66711 9.16151 9.84757 8.84589 9.937 8.50001L11.519 2.36501C11.5484 2.25992 11.6114 2.16735 11.6983 2.1014C11.7853 2.03545 11.8914 1.99976 12.0005 1.99976C12.1096 1.99976 12.2157 2.03545 12.3027 2.1014C12.3896 2.16735 12.4526 2.25992 12.482 2.36501L14.063 8.50001C14.1523 8.84608 14.3327 9.1619 14.5854 9.41462C14.8381 9.66734 15.1539 9.84773 15.5 9.93701L21.635 11.518C21.7405 11.5471 21.8335 11.61 21.8998 11.6971C21.9661 11.7841 22.0021 11.8906 22.0021 12C22.0021 12.1094 21.9661 12.2159 21.8998 12.3029C21.8335 12.39 21.7405 12.4529 21.635 12.482L15.5 14.063C15.1539 14.1523 14.8381 14.3327 14.5854 14.5854C14.3327 14.8381 14.1523 15.1539 14.063 15.5L12.481 21.635C12.4516 21.7401 12.3886 21.8327 12.3017 21.8986C12.2147 21.9646 12.1086 22.0003 11.9995 22.0003C11.8904 22.0003 11.7843 21.9646 11.6973 21.8986C11.6104 21.8327 11.5474 21.7401 11.518 21.635L9.937 15.5Z"
              stroke="black"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t("actions.createNew")}</span>
        </button>
      </div>
    </div>,
    document.body
  );
};

export default MyMealsStickyFooter;
